import { openDB, IDBPDatabase } from 'idb'
import { Entity, Thread, QANode, AppState } from '@/types'

const DB_NAME = 'qa-threads-db'
const DB_VERSION = 2
const ENTITIES_STORE = 'entities'
const THREADS_STORE = 'threads'
const QA_NODES_STORE = 'qa-nodes'
const SETTINGS_STORE = 'settings'
const PREFERENCES_STORE = 'preferences'

export class Storage {
  private db: IDBPDatabase | null = null

  async init() {
    try {
      this.db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion) {
          // Create entities store
          if (!db.objectStoreNames.contains(ENTITIES_STORE)) {
            db.createObjectStore(ENTITIES_STORE, { keyPath: 'id' })
          }
          
          // Create threads store
          if (!db.objectStoreNames.contains(THREADS_STORE)) {
            db.createObjectStore(THREADS_STORE, { keyPath: 'id' })
          }
          
          // Create QA nodes store
          if (!db.objectStoreNames.contains(QA_NODES_STORE)) {
            db.createObjectStore(QA_NODES_STORE, { keyPath: 'id' })
          }
          
          // Create settings store
          if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
            db.createObjectStore(SETTINGS_STORE)
          }
          
          // Create preferences store for language and other user preferences
          if (!db.objectStoreNames.contains(PREFERENCES_STORE)) {
            db.createObjectStore(PREFERENCES_STORE)
          }
          
          // Migration from old single-store format
          if (oldVersion === 1) {
            const oldStoreName = 'app-state'
            if (db.objectStoreNames.contains(oldStoreName)) {
              // We'll handle migration in the migration method
            }
          }
        },
      })
      
      // Perform migration if needed
      await this.migrateFromOldFormat()
    } catch (error) {
      console.error('Failed to initialize IndexedDB:', error)
    }
  }

  private async migrateFromOldFormat() {
    try {
      // Check if old format data exists
      const oldAppState = await this.get<AppState>('app-state')
      if (oldAppState && Object.keys(oldAppState.entities || {}).length > 0) {
        console.log('Migrating from old storage format...')
        
        // Migrate entities
        for (const entity of Object.values(oldAppState.entities)) {
          await this.saveEntity(entity)
        }
        
        // Migrate threads
        for (const thread of Object.values(oldAppState.threads)) {
          await this.saveThread(thread)
        }
        
        // Migrate QA nodes
        for (const qaNode of Object.values(oldAppState.qaNodes)) {
          await this.saveQANode(qaNode)
        }
        
        // Migrate settings
        if (oldAppState.settings) {
          await this.saveSettings(oldAppState.settings)
        }
        
        // Remove old data
        const oldStoreName = 'app-state'
        if (this.db?.objectStoreNames.contains(oldStoreName)) {
          const tx = this.db.transaction(oldStoreName, 'readwrite')
          await tx.objectStore(oldStoreName).clear()
          await tx.done
        }
        
        console.log('Migration completed successfully')
      }
    } catch (error) {
      console.error('Migration failed:', error)
    }
  }

  // Legacy method for backward compatibility
  async get<T>(key: string): Promise<T | undefined> {
    try {
      if (!this.db) await this.init()
      
      // Check if it's trying to get the old app-state
      if (key === 'app-state') {
        return await this.getAppState() as T
      }
      
      // For other keys, try settings store first
      return await this.db?.get(SETTINGS_STORE, key)
    } catch (error) {
      console.error('Failed to get from storage:', error)
      return undefined
    }
  }

  // Legacy method for backward compatibility
  async set<T>(key: string, value: T): Promise<void> {
    try {
      if (!this.db) await this.init()
      
      // If it's the app-state, decompose it into separate stores
      if (key === 'app-state') {
        await this.saveAppState(value as AppState)
        return
      }
      
      // For other keys, save to settings store
      await this.db?.put(SETTINGS_STORE, value, key)
    } catch (error) {
      console.error('Failed to save to storage:', error)
    }
  }

  async clear(): Promise<void> {
    try {
      if (!this.db) await this.init()
      
      // Clear all stores
      const stores = [ENTITIES_STORE, THREADS_STORE, QA_NODES_STORE, SETTINGS_STORE, PREFERENCES_STORE]
      for (const storeName of stores) {
        if (this.db?.objectStoreNames.contains(storeName)) {
          const tx = this.db.transaction(storeName, 'readwrite')
          await tx.objectStore(storeName).clear()
          await tx.done
        }
      }
    } catch (error) {
      console.error('Failed to clear storage:', error)
    }
  }

  async clearData(): Promise<void> {
    try {
      if (!this.db) await this.init()

      const stores = [ENTITIES_STORE, THREADS_STORE, QA_NODES_STORE, SETTINGS_STORE]
      for (const storeName of stores) {
        if (this.db?.objectStoreNames.contains(storeName)) {
          const tx = this.db.transaction(storeName, 'readwrite')
          await tx.objectStore(storeName).clear()
          await tx.done
        }
      }
    } catch (error) {
      console.error('Failed to clear data stores:', error)
    }
  }

  async replaceAppState(appState: AppState): Promise<void> {
    try {
      await this.clearData()
      await this.saveAppState(appState)
    } catch (error) {
      console.error('Failed to replace app state:', error)
    }
  }

  // New granular methods
  async saveEntity(entity: Entity): Promise<void> {
    try {
      if (!this.db) await this.init()
      await this.db?.put(ENTITIES_STORE, entity)
    } catch (error) {
      console.error('Failed to save entity:', error)
    }
  }

  async getEntity(id: string): Promise<Entity | undefined> {
    try {
      if (!this.db) await this.init()
      return await this.db?.get(ENTITIES_STORE, id)
    } catch (error) {
      console.error('Failed to get entity:', error)
      return undefined
    }
  }

  async getAllEntities(): Promise<Entity[]> {
    try {
      if (!this.db) await this.init()
      return await this.db?.getAll(ENTITIES_STORE) || []
    } catch (error) {
      console.error('Failed to get all entities:', error)
      return []
    }
  }

  async deleteEntity(id: string): Promise<void> {
    try {
      if (!this.db) await this.init()
      await this.db?.delete(ENTITIES_STORE, id)
    } catch (error) {
      console.error('Failed to delete entity:', error)
    }
  }

  async saveThread(thread: Thread): Promise<void> {
    try {
      if (!this.db) await this.init()
      await this.db?.put(THREADS_STORE, thread)
    } catch (error) {
      console.error('Failed to save thread:', error)
    }
  }

  async getThread(id: string): Promise<Thread | undefined> {
    try {
      if (!this.db) await this.init()
      return await this.db?.get(THREADS_STORE, id)
    } catch (error) {
      console.error('Failed to get thread:', error)
      return undefined
    }
  }

  async getAllThreads(): Promise<Thread[]> {
    try {
      if (!this.db) await this.init()
      return await this.db?.getAll(THREADS_STORE) || []
    } catch (error) {
      console.error('Failed to get all threads:', error)
      return []
    }
  }

  async deleteThread(id: string): Promise<void> {
    try {
      if (!this.db) await this.init()
      await this.db?.delete(THREADS_STORE, id)
    } catch (error) {
      console.error('Failed to delete thread:', error)
    }
  }

  async saveQANode(qaNode: QANode): Promise<void> {
    try {
      if (!this.db) await this.init()
      await this.db?.put(QA_NODES_STORE, qaNode)
    } catch (error) {
      console.error('Failed to save QA node:', error)
    }
  }

  async getQANode(id: string): Promise<QANode | undefined> {
    try {
      if (!this.db) await this.init()
      return await this.db?.get(QA_NODES_STORE, id)
    } catch (error) {
      console.error('Failed to get QA node:', error)
      return undefined
    }
  }

  async getAllQANodes(): Promise<QANode[]> {
    try {
      if (!this.db) await this.init()
      return await this.db?.getAll(QA_NODES_STORE) || []
    } catch (error) {
      console.error('Failed to get all QA nodes:', error)
      return []
    }
  }

  async deleteQANode(id: string): Promise<void> {
    try {
      if (!this.db) await this.init()
      await this.db?.delete(QA_NODES_STORE, id)
    } catch (error) {
      console.error('Failed to delete QA node:', error)
    }
  }

  async saveSettings(settings: any): Promise<void> {
    try {
      if (!this.db) await this.init()
      await this.db?.put(SETTINGS_STORE, settings, 'app-settings')
    } catch (error) {
      console.error('Failed to save settings:', error)
    }
  }

  async getSettings(): Promise<any> {
    try {
      if (!this.db) await this.init()
      return await this.db?.get(SETTINGS_STORE, 'app-settings')
    } catch (error) {
      console.error('Failed to get settings:', error)
      return undefined
    }
  }

  async savePreference(key: string, value: any): Promise<void> {
    try {
      if (!this.db) await this.init()
      await this.db?.put(PREFERENCES_STORE, value, key)
    } catch (error) {
      console.error('Failed to save preference:', error)
    }
  }

  async getPreference(key: string): Promise<any> {
    try {
      if (!this.db) await this.init()
      return await this.db?.get(PREFERENCES_STORE, key)
    } catch (error) {
      console.error('Failed to get preference:', error)
      return undefined
    }
  }

  // Helper method to reconstruct AppState from separate stores
  private async getAppState(): Promise<AppState> {
    try {
      const [entities, threads, qaNodes, settings] = await Promise.all([
        this.getAllEntities(),
        this.getAllThreads(), 
        this.getAllQANodes(),
        this.getSettings()
      ])

      // Convert arrays to records
      const entitiesRecord: Record<string, Entity> = {}
      entities.forEach(entity => {
        entitiesRecord[entity.id] = entity
      })

      const threadsRecord: Record<string, Thread> = {}
      threads.forEach(thread => {
        threadsRecord[thread.id] = thread
      })

      const qaNodesRecord: Record<string, QANode> = {}
      qaNodes.forEach(qaNode => {
        qaNodesRecord[qaNode.id] = qaNode
      })

      return {
        entities: entitiesRecord,
        threads: threadsRecord,
        qaNodes: qaNodesRecord,
        settings: settings || { shortcutsEnabled: true, verboseMode: true }
      }
    } catch (error) {
      console.error('Failed to reconstruct app state:', error)
      return {
        entities: {},
        threads: {},
        qaNodes: {},
        settings: { shortcutsEnabled: true, verboseMode: true }
      }
    }
  }

  // Helper method to save AppState to separate stores
  private async saveAppState(appState: AppState): Promise<void> {
    try {
      // Save entities
      for (const entity of Object.values(appState.entities)) {
        await this.saveEntity(entity)
      }

      // Save threads
      for (const thread of Object.values(appState.threads)) {
        await this.saveThread(thread)
      }

      // Save QA nodes
      for (const qaNode of Object.values(appState.qaNodes)) {
        await this.saveQANode(qaNode)
      }

      // Save settings
      if (appState.settings) {
        await this.saveSettings(appState.settings)
      }
    } catch (error) {
      console.error('Failed to save app state:', error)
    }
  }

  /**
   * Export the entire IndexedDB database generically, including all current object stores
   * and their records, so future stores/fields are automatically included without code changes.
   */
  async exportDatabase(): Promise<{
    name: string
    version: number
    exportedAt: string
    stores: Array<{
      name: string
      keyPath: any
      autoIncrement?: boolean
      indexes?: Array<{ name: string; keyPath: any; unique?: boolean; multiEntry?: boolean }>
      records: any[]
    }>
  }> {
    if (!this.db) await this.init()
    const db = this.db!

    const storeNames = Array.from((db.objectStoreNames as unknown as string[]) || [])
    const storesDump: Array<{
      name: string
      keyPath: any
      autoIncrement?: boolean
      indexes?: Array<{ name: string; keyPath: any; unique?: boolean; multiEntry?: boolean }>
      records: any[]
    }> = []

    for (const storeName of storeNames) {
      try {
        const tx = db.transaction(storeName, 'readonly')
        const store = tx.objectStore(storeName)
        const records = await store.getAll()

        // Collect store metadata defensively
        const keyPath = (store as any).keyPath ?? null
        const autoIncrement = (store as any).autoIncrement

        // Index metadata (best-effort)
        const idxNames: string[] = Array.from(((store as any).indexNames || []) as any)
        const indexes = idxNames.map((idxName) => {
          try {
            const idx = (store as any).index(idxName)
            return {
              name: idxName,
              keyPath: (idx as any)?.keyPath ?? null,
              unique: (idx as any)?.unique,
              multiEntry: (idx as any)?.multiEntry,
            }
          } catch {
            return { name: idxName, keyPath: null }
          }
        })

        await tx.done

        storesDump.push({
          name: storeName,
          keyPath,
          autoIncrement,
          indexes,
          records,
        })
      } catch (err) {
        console.error(`Failed to export store ${storeName}:`, err)
        // Push a placeholder so the dump still reflects store presence
        storesDump.push({ name: storeName, keyPath: null, records: [] })
      }
    }

    return {
      name: DB_NAME,
      version: db.version,
      exportedAt: new Date().toISOString(),
      stores: storesDump,
    }
  }

  /**
   * Import an entire database dump created by exportDatabase(). This will DELETE the existing
   * database and recreate all object stores, indexes, and records exactly as described in the dump.
   */
  async importDatabase(dump: {
    name: string
    version: number
    stores: Array<{
      name: string
      keyPath: any
      autoIncrement?: boolean
      indexes?: Array<{ name: string; keyPath: any; unique?: boolean; multiEntry?: boolean }>
      records: any[]
    }>
  }): Promise<void> {
    // Basic validation
    if (!dump || typeof dump !== 'object' || !Array.isArray(dump.stores)) {
      throw new Error('Invalid database dump format')
    }

    // Close current connection (if any)
    try { this.db?.close() } catch { /* noop */ }
    this.db = null

    // Delete existing DB
    await new Promise<void>((resolve, reject) => {
      const req = indexedDB.deleteDatabase(DB_NAME)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error as any)
      // If blocked, resolve anyway; a future refresh will complete cleanup
      req.onblocked = () => resolve()
    })

    // Recreate DB following dump schema
    const targetVersion = typeof dump.version === 'number' && dump.version > 0 ? dump.version : DB_VERSION
    const db = await openDB(DB_NAME, targetVersion, {
      upgrade(upgradeDb) {
        // Create all stores with provided options
        for (const storeDef of dump.stores) {
          try {
            if (upgradeDb.objectStoreNames.contains(storeDef.name)) {
              upgradeDb.deleteObjectStore(storeDef.name)
            }
            const os = upgradeDb.createObjectStore(storeDef.name, {
              keyPath: storeDef.keyPath ?? undefined,
              autoIncrement: Boolean(storeDef.autoIncrement)
            })
            // Create indexes (best-effort)
            const idxs = Array.isArray(storeDef.indexes) ? storeDef.indexes : []
            for (const idx of idxs) {
              try {
                os.createIndex(idx.name, idx.keyPath, { unique: Boolean(idx.unique), multiEntry: Boolean(idx.multiEntry) })
              } catch { /* noop */ }
            }
          } catch { /* noop */ }
        }
      }
    })

    // Insert records
    for (const storeDef of dump.stores) {
      try {
        const tx = db.transaction(storeDef.name, 'readwrite')
        const store = tx.objectStore(storeDef.name)
        const records = Array.isArray(storeDef.records) ? storeDef.records : []
        for (const rec of records) {
          try { await store.put(rec) } catch { /* noop */ }
        }
        await tx.done
      } catch (err) {
        console.error('Failed importing records for store', storeDef.name, err)
      }
    }

    this.db = db
  }
}

export const storage = new Storage()